import type { TaxAddress } from "@townsy/contracts";

/**
 * ADR-0046: the buyer's sales-tax location in the checkout (country + postal code). Only these two fields are asked
 * for; the server checks them again (domain normalizeTaxAddress) and Stripe Tax decides the tax. Kept in memory for
 * this browser tab only (not stored), so a second purchase in the same visit does not ask again.
 */

/** ISO 3166-1 alpha-2 codes (officially assigned). Names come from the browser (Intl.DisplayNames). */
const CODES =
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");

/** Countries whose postal code is required (Stripe Tax needs it there for the right rate). */
export const POSTAL_REQUIRED = new Set(["US", "CA"]);

export interface CountryOption {
  code: string;
  name: string;
}

/** United States first (Townsy's sales tax is US sales tax), then every other country by name. */
export function countryOptions(locale = "en"): CountryOption[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    names = null;
  }
  const name = (code: string) => {
    try {
      return names?.of(code) ?? code;
    } catch {
      return code;
    }
  };
  const rest = CODES.filter((c) => c !== "US")
    .map((code) => ({ code, name: name(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return [{ code: "US", name: name("US") }, ...rest];
}

/** The same checks as the server, so the form explains a problem before any request. Null = valid. */
export function taxAddressProblem(address: { country: string; postalCode: string | null | undefined }): string | null {
  const country = address.country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(country)) return "Choose your country.";
  const postal = (address.postalCode ?? "").trim().toUpperCase();
  if (!postal && POSTAL_REQUIRED.has(country)) return country === "US" ? "Enter your ZIP code." : "Enter your postal code.";
  if (postal && !/^[A-Z0-9][A-Z0-9 -]{0,11}$/.test(postal)) return "Enter a valid postal code.";
  if (country === "US" && postal && !/^\d{5}(-\d{4})?$/.test(postal)) return "A US ZIP code has 5 digits.";
  return null;
}

let remembered: TaxAddress | null = null;

/** The location given earlier in this tab (memory only; never written to storage). */
export function rememberedTaxAddress(): TaxAddress | null {
  return remembered;
}

export function rememberTaxAddress(address: TaxAddress | null): void {
  remembered = address ? { country: address.country, postalCode: address.postalCode ?? null } : null;
}
